/**
 * AdminReviewsPage — iter1114
 *
 * Admin-only moderation UI for user reviews. Lists all reviews with
 * filter tabs (pending / approved / rejected / private) and allows
 * approve/reject + display-name override before publishing to Landing.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { MobileTopBar } from "@/components/MobileTopBar";
import { Button } from "@/components/ui/button";
import { Loader2, Check, X, Star, Edit3 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "pending",  label: "Чакащи",     color: "#F59E0B" },
  { id: "approved", label: "Одобрени",   color: "#10B981" },
  { id: "rejected", label: "Отхвърлени", color: "#EF4444" },
  { id: "private",  label: "Лични",      color: "#64748B" },
  { id: "all",      label: "Всички",     color: "#0EA5E9" },
];

export default function AdminReviewsPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState("");

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, { params: { status } });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error("Грешка при зареждането");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const moderate = async (id, action, extra = {}) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}`, { action, ...extra });
      toast.success(
        action === "approve" ? "Одобрено" : action === "reject" ? "Отхвърлено" : "Записано"
      );
      setEditingId(null);
      await load(tab);
    } catch {
      toast.error("Грешка");
    }
  };

  const bg = isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900";
  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`min-h-screen ${bg}`} data-testid="admin-reviews-page">
      <MobileTopBar title="Отзиви (admin)" fallbackTo="/admin" testid="admin-reviews-topbar" />
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <h1 className="text-xl font-bold mb-4">Преглед и одобряване на отзиви</h1>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="review-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                tab === t.id
                  ? "text-white border-transparent"
                  : isDark
                  ? "bg-slate-800/50 border-slate-700 hover:border-slate-500"
                  : "bg-white border-slate-200 hover:border-slate-400"
              }`}
              style={tab === t.id ? { backgroundColor: t.color } : {}}
              data-testid={`review-tab-${t.id}`}
            >
              {t.label}
              {counts[t.id] != null && (
                <span className="ml-1.5 opacity-70">{counts[t.id]}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
          </div>
        ) : items.length === 0 ? (
          <div className={`p-6 rounded-xl border text-sm text-center opacity-60 ${cardCls}`}>
            Няма отзиви в тази категория.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((r) => (
              <div
                key={r.id}
                className={`p-4 rounded-xl border ${cardCls}`}
                data-testid={`review-row-${r.id}`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">{r.user_name}</span>
                      <span className="opacity-50 text-[11px]">{r.user_email}</span>
                      {r.allow_publish && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500 font-semibold">
                          Съгласие ✓
                        </span>
                      )}
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/15 opacity-70">
                        {r.moderation_status}
                      </span>
                    </div>
                    {r.rating && (
                      <div className="flex items-center gap-0.5 mt-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            className={`w-3.5 h-3.5 ${
                              s <= r.rating ? "text-amber-500 fill-amber-500" : "opacity-20"
                            }`}
                          />
                        ))}
                      </div>
                    )}
                    <p className="text-xs mt-2 opacity-85 leading-snug whitespace-pre-wrap">
                      {r.text || <em className="opacity-50">(без текст)</em>}
                    </p>
                    <div className="text-[10px] opacity-50 mt-2">
                      {new Date(r.created_at).toLocaleString("bg-BG")}
                    </div>

                    {editingId === r.id ? (
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="text"
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          placeholder="Име за публично показване"
                          className="flex-1 text-xs p-1.5 rounded border bg-transparent"
                          data-testid={`display-name-input-${r.id}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => moderate(r.id, "edit", { display_name_override: draftName })}
                          data-testid={`save-display-name-${r.id}`}
                        >
                          Запиши
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                          Отказ
                        </Button>
                      </div>
                    ) : (
                      r.display_name_override && (
                        <div className="text-[11px] mt-1 opacity-70">
                          Публично име:{" "}
                          <span className="font-semibold">{r.display_name_override}</span>
                        </div>
                      )
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => {
                        setEditingId(r.id);
                        setDraftName(r.display_name_override || "");
                      }}
                      className="p-1.5 rounded hover:bg-slate-500/15"
                      title="Промени име"
                      data-testid={`edit-name-btn-${r.id}`}
                    >
                      <Edit3 className="w-3.5 h-3.5 opacity-70" />
                    </button>
                    {r.moderation_status !== "approved" && (
                      <button
                        onClick={() => moderate(r.id, "approve")}
                        className="p-1.5 rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-500"
                        title="Одобри"
                        data-testid={`approve-btn-${r.id}`}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {r.moderation_status !== "rejected" && (
                      <button
                        onClick={() => moderate(r.id, "reject")}
                        className="p-1.5 rounded bg-rose-500/15 hover:bg-rose-500/25 text-rose-500"
                        title="Отхвърли"
                        data-testid={`reject-btn-${r.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
